import { listProductCategory, listSolutionCategory, listNewsCategory, listSiteNavItem, listProduct } from '@/utils/http'

import { mapNavCategories, mapNewsNavCategories, mapSiteNavItems, mapProductMegaNav, mapSolutionMegaNav, unwrapRows, syncNewsNavCategories } from '@/utils/content'

import { getApiLangType } from '@/utils/langApi'

import { getDefaultLocale } from '@/i18n'

import zh from '@/i18n/zh'

import en from '@/i18n/en'

import {

  PRODUCT_NAV, SOLUTION_NAV, APPLICATION_NAV,

  SERVICE_NAV_LINKS, ABOUT_NAV_LINKS, REMOVED_ABOUT_NAV_SLUGS,

  SOLUTION_NAV_ZH, APPLICATION_NAV_ZH

} from '@/constants/hiraysHomeStatic'

import { NEWS_NAV } from '@/constants/hiraysData'



import { resolveI18nNavLabel, resolveNavItemName } from '@/utils/navLabel'



let navCache = null

let navLoadGen = 0

let navInflight = null



function withImages(items) {
  return (items || []).map(item => ({
    ...item,
    image: item.image || ''
  }))
}



function pickI18nLabel(messages, group, slug, fallback) {

  const g = messages[group]

  return (g && g[slug]) || fallback

}



function localizedSlugNav(locale, group, links) {

  const messages = locale === 'en' ? en : zh

  return (links || []).map(item => ({

    ...item,

    name: pickI18nLabel(messages, group, item.slug, item.name)

  }))

}



function localizeNavList(items, locale, group) {

  const messages = locale === 'en' ? en : zh

  return (items || []).map(item => ({

    ...item,

    name: resolveNavItemName(messages, group, item)

  }))

}



function localizeProductMegaNav(items, locale) {

  const messages = locale === 'en' ? en : zh

  return (items || []).map(cat => ({

    ...cat,

    name: resolveNavItemName(messages, 'productNav', cat),

    products: (cat.products || []).map(p => ({

      ...p,

      name: p.name || ''

    }))

  }))

}



/** Category shell only — used as API failure fallback, not for first paint. */

function staticProductMegaNav() {

  return PRODUCT_NAV.map((c, i) => ({

    id: i + 1,

    slug: c.slug,

    name: c.name,

    products: []

  }))

}



function filterAboutNav(list) {
  return (list || []).filter(item => !REMOVED_ABOUT_NAV_SLUGS.includes(item.slug))
}



function staticNav(locale) {

  const activeLocale = locale || getDefaultLocale()

  const isZh = activeLocale === 'zh'

  const applicationItems = withImages(isZh ? APPLICATION_NAV_ZH : APPLICATION_NAV)

  return {

    productNav: localizeNavList(PRODUCT_NAV, activeLocale, 'productNav'),

    productMegaNav: localizeProductMegaNav(staticProductMegaNav(), activeLocale),

    solutionMegaNav: [],

    applicationMegaNav: localizeNavList(applicationItems, activeLocale, 'applicationNav'),

    solutionNav: [],

    applicationNav: localizeNavList(applicationItems, activeLocale, 'applicationNav'),

    newsNav: localizedSlugNav(activeLocale, 'newsNav', NEWS_NAV.map(n => ({ slug: n.slug, name: n.title, newsType: n.type }))),

    serviceNav: localizedSlugNav(activeLocale, 'serviceNav', SERVICE_NAV_LINKS),

    aboutNav: localizedSlugNav(activeLocale, 'aboutNav', ABOUT_NAV_LINKS)

  }

}



/**
 * First-paint nav: no hardcoded product/application mega data.
 * Avoids flashing English PRODUCT_NAV shells before CMS responds.
 */
export function getInitialSiteNav(locale) {

  const activeLocale = locale || getDefaultLocale()

  return {

    productNav: [],

    productMegaNav: [],

    solutionMegaNav: [],

    applicationMegaNav: [],

    solutionNav: [],

    applicationNav: [],

    newsNav: localizedSlugNav(activeLocale, 'newsNav', NEWS_NAV.map(n => ({ slug: n.slug, name: n.title, newsType: n.type }))),

    serviceNav: localizedSlugNav(activeLocale, 'serviceNav', SERVICE_NAV_LINKS),

    aboutNav: localizedSlugNav(activeLocale, 'aboutNav', ABOUT_NAV_LINKS)

  }

}



export function resetSiteNavCache() {

  navCache = null

  navLoadGen += 1

  navInflight = null

}



function isCmsBackedList(items) {

  return (items || []).some(item => item && item.id != null)

}



function isCacheValid(cached, activeLocale) {

  if (!cached || cached.locale !== activeLocale) return false

  const data = cached.data

  return isCmsBackedList(data.solutionMegaNav)

    || isCmsBackedList(data.serviceNav)

    || isCmsBackedList(data.aboutNav)

    || isCmsBackedList(data.productMegaNav)

}



export function hasCmsNavData(nav) {

  if (!nav) return false

  return isCmsBackedList(nav.solutionMegaNav)

    || isCmsBackedList(nav.serviceNav)

    || isCmsBackedList(nav.aboutNav)

}



function settledRows(result) {

  if (!result || result.status !== 'fulfilled') return []

  return unwrapRows(result.value)

}



async function buildSiteNav(activeLocale) {

  const langParams = { eType: getApiLangType(activeLocale) }

  const fallback = staticNav(activeLocale)



  const results = await Promise.allSettled([

    listProductCategory({ status: 1, pageNum: 1, pageSize: 50, ...langParams }, activeLocale),

    listProduct({ status: 1, pageNum: 1, pageSize: 500, ...langParams }, activeLocale),

    listSolutionCategory({ categoryType: 1, status: 1, pageNum: 1, pageSize: 50, ...langParams }, activeLocale),

    listNewsCategory({ status: 1, pageNum: 1, pageSize: 50, ...langParams }, activeLocale),

    listSiteNavItem({ status: 1, pageNum: 1, pageSize: 100, ...langParams }, activeLocale)

  ])



  const categoryRows = settledRows(results[0])

  const productRows = settledRows(results[1])

  const applicationRows = settledRows(results[2])

  const newsRows = settledRows(results[3])

  const siteNavRows = settledRows(results[4])



  const productNav = mapNavCategories(categoryRows, 'categorySlug', 'categoryName')

  const productMegaNav = mapProductMegaNav(categoryRows, productRows)

  const applicationMegaNav = mapSolutionMegaNav(applicationRows)

  const applicationNav = applicationMegaNav.map(({ id, slug, name }) => ({ id, slug, name }))

  const newsNav = mapNewsNavCategories(newsRows)

  const serviceNav = mapSiteNavItems(siteNavRows, 0)

  const aboutNav = mapSiteNavItems(siteNavRows, 1)

  syncNewsNavCategories(newsNav.length ? newsNav : fallback.newsNav)



  return {

    productNav: localizeNavList(productNav, activeLocale, 'productNav'),

    productMegaNav: localizeProductMegaNav(productMegaNav, activeLocale),

    solutionMegaNav: [],

    applicationMegaNav: localizeNavList(applicationMegaNav, activeLocale, 'applicationNav'),

    solutionNav: [],

    applicationNav: localizeNavList(applicationNav, activeLocale, 'applicationNav'),

    newsNav: newsNav.length ? newsNav : fallback.newsNav,

    serviceNav: serviceNav.length ? localizeNavList(serviceNav, activeLocale, 'serviceNav') : fallback.serviceNav,

    aboutNav: filterAboutNav(aboutNav.length ? localizeNavList(aboutNav, activeLocale, 'aboutNav') : fallback.aboutNav)

  }

}



export async function fetchSiteNav(force = false, locale) {

  const activeLocale = locale || getDefaultLocale()



  if (!force && navCache && isCacheValid(navCache, activeLocale)) {

    return navCache.data

  }



  if (navInflight && navInflight.locale === activeLocale) {

    return navInflight.promise

  }



  const gen = ++navLoadGen

  const promise = buildSiteNav(activeLocale)

    .then(data => {

      if (gen === navLoadGen) {

        navCache = { locale: activeLocale, data }

      }

      return data

    })

    .finally(() => {

      if (navInflight && navInflight.promise === promise) {

        navInflight = null

      }

    })



  navInflight = { locale: activeLocale, promise }

  return promise

}



/** Load solution/application mega nav directly from CMS (used by header). */

export async function fetchSolutionMegaNav(locale, categoryType = 0) {

  const activeLocale = locale || getDefaultLocale()

  const langParams = {

    eType: getApiLangType(activeLocale),

    categoryType,

    status: 1,

    pageNum: 1,

    pageSize: 50

  }

  const res = await listSolutionCategory(langParams, activeLocale)

  const rows = unwrapRows(res)

  const mega = mapSolutionMegaNav(rows)

  const group = Number(categoryType) === 1 ? 'applicationNav' : 'solutionNav'

  return localizeNavList(mega, activeLocale, group)

}


